import React, { useEffect, useState } from "react";
import { getEmergencyFund, setMonthlyExpenses } from "../../api/emergencyFundClient";
import { formatCurrencyCompact } from "../../utils/formatters";
import ChartLoader from "../ui/ChartLoader";

const STATUS_CONFIG = {
    HEALTHY: {
        label: "Healthy",
        color: "text-success-600",
        description: "6+ months covered",
    },
    BUILDING: {
        label: "Building",
        color: "text-warning-600",
        description: "3-6 months covered",
    },
    LOW: {
        label: "Low",
        color: "text-danger-500",
        description: "Less than 3 months",
    },
    NOT_SET: {
        label: "Not Set",
        color: "text-gray-500",
        description: "Set your monthly expenses",
    },
};

export default function EmergencyFundCard({ emergencyFundData: propData, onUpdate, readOnly = false }) {
    const [data, setData] = useState(propData || null);
    const [loading, setLoading] = useState(!propData);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [expenseInput, setExpenseInput] = useState("");
    const [saving, setSaving] = useState(false);

    const isDemoMode = !!onUpdate;

    useEffect(() => {
        if (propData) {
            setData(propData);
            setExpenseInput(propData.monthlyExpenses || "");
            setLoading(false);
            return;
        }
        fetchData();
    }, [propData]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const resp = await getEmergencyFund();
            setData(resp);
            setExpenseInput(resp.monthlyExpenses || "");
        } catch (e) {
            console.error("Error fetching emergency fund:", e);
            setError("Could not load emergency fund data");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        const amount = parseFloat(expenseInput);
        if (isNaN(amount) || amount <= 0) {
            return;
        }

        if (isDemoMode) {
            onUpdate(amount);
            setShowModal(false);
            return;
        }

        try {
            setSaving(true);
            const resp = await setMonthlyExpenses(amount);
            setData(resp);
            setShowModal(false);
        } catch (e) {
            console.error("Error saving monthly expenses:", e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="card p-5">
                <div className="h-4 bg-gray-100 rounded w-32 mb-3 animate-pulse"></div>
                <ChartLoader height={48} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-5">
                <p className="text-sm text-gray-500">{error}</p>
            </div>
        );
    }

    const status = STATUS_CONFIG[data?.status] || STATUS_CONFIG.NOT_SET;
    const monthsCovered = data?.monthsCovered || 0;
    const isSetUp = data?.status !== "NOT_SET";

    const progressPercent = Math.min((monthsCovered / 6) * 100, 100);
    const barColor =
        monthsCovered > 3 ? "bg-success-500" :
        monthsCovered >= 1 ? "bg-warning-500" :
        "bg-danger-500";

    return (
        <>
            <div className="card p-5">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-700">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
                            </svg>
                        </span>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Emergency fund</h3>
                            <p className="text-xs text-gray-500">{status.description}</p>
                        </div>
                    </div>
                    {!readOnly && (
                        <button
                            onClick={() => {
                                setExpenseInput(data?.monthlyExpenses || "");
                                setShowModal(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                            title="Set monthly expenses"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                        </button>
                    )}
                </div>

                {isSetUp ? (
                    <>
                        <div className="flex items-baseline gap-2 mb-3">
                            <span className={`text-2xl font-semibold tracking-tight ${status.color}`}>
                                {monthsCovered.toFixed(1)}
                            </span>
                            <span className="text-sm text-gray-500">months covered</span>
                        </div>

                        <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gray-300 z-10" title="3 months" />
                            <div className="absolute top-0 bottom-0 left-full w-px bg-gray-300 z-10" title="6 months" />

                            <div
                                className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>

                        <div className="flex justify-between text-xs text-gray-500">
                            <span>0</span>
                            <span>3 mo</span>
                            <span>6 mo</span>
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between text-xs">
                            <span className="text-gray-500">Cash balance</span>
                            <span className="text-gray-900 font-medium">{formatCurrencyCompact(data.cashBalance)}</span>
                        </div>
                        <div className="flex justify-between text-xs mt-1">
                            <span className="text-gray-500">Monthly expenses</span>
                            <span className="text-gray-900 font-medium">{formatCurrencyCompact(data.monthlyExpenses)}</span>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-sm text-gray-600 mb-3">
                            Set your monthly expenses to track your emergency fund coverage.
                        </p>
                        <button
                            onClick={() => setShowModal(true)}
                            className="btn-primary text-sm"
                        >
                            Set monthly expenses
                        </button>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-gray-900/40 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
                    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly expenses</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Enter your average monthly expenses to calculate how many months your cash balance covers.
                        </p>
                        <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-2">Amount</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                <input
                                    type="number"
                                    value={expenseInput}
                                    onChange={(e) => setExpenseInput(e.target.value)}
                                    className="input w-full pl-7"
                                    placeholder="3500"
                                    min="1"
                                    step="100"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handleSave}
                                disabled={saving || !expenseInput || parseFloat(expenseInput) <= 0}
                                className="btn-primary flex-1"
                            >
                                {saving ? "Saving..." : "Save"}
                            </button>
                            <button
                                onClick={() => setShowModal(false)}
                                disabled={saving}
                                className="btn-secondary flex-1"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
